import { useFormContext } from 'react-hook-form';
import styles from './styles.module.scss';
import {
  ResendCodeButton,
  ValidateCodeInput,
} from '@starsoft/common/components';
import { useUserEmailSendCode } from '@starsoft/common/api';
import { UserEmailVerifyInputDto } from '@starsoft/common/dto';
import { UserEmailFormVerifyProps } from './props';

/**
 * UserEmailFormVerify Component
 * This component is responsible for rendering the user email verification form.
 * It includes an input for the verification code and a button to resend the code.
 *
 * @param {UserEmailFormVerifyProps} props - The properties for the component.
 * @param {boolean} props.isContainerColor - Indicates if the container should have a color.
 *
 * @returns {JSX.Element} The rendered user email verification form component.
 *
 * @example
 * // Usage example:
 * <UserEmailFormVerify isContainerColor={true} />
 */
export default function UserEmailFormVerify({
  isContainerColor,
}: UserEmailFormVerifyProps) {
  const { setValue, watch } = useFormContext<UserEmailVerifyInputDto>();
  const emailId: string = watch('emailId');
  const { sendCode, isPending, isSuccess } = useUserEmailSendCode();

  /**
   * resendCode Function
   * This function triggers the sending of a verification code to the user's email.
   * It uses the emailId from the form context to identify the user.
   *
   * @example
   * resendCode(); // Sends a verification code to the email associated with the current emailId
   */
  function resendCode() {
    sendCode({
      emailId: emailId?.toString(),
    });
  }

  /**
   * onChange Function
   * This function updates the form context with the new verification code value.
   *
   * @param {string} value - The new value for the verification code.
   *
   * @example
   * onChange('123456'); // Sets the verification code in the form context to '123456'
   */
  function onChange(value: string) {
    setValue('code', value);
  }

  return (
    <div className={styles.container}>
      <ValidateCodeInput
        setValue={onChange}
        isContainerColor={isContainerColor}
      />
      <ResendCodeButton
        onClick={resendCode}
        isLoading={isPending}
        isSuccess={isSuccess}
      />
    </div>
  );
}
